import { useEffect } from 'react';
import { injectHeadScript } from '../util/inject-head-script';
import {
    getAdvertisingWindow,
    type ObApiStub,
} from '../util/advertising-window';
import { isConfiguredPixelId } from './is-configured-pixel-id';

const OUTBRAIN_SRC = 'https://amplify.outbrain.com/cp/obtp.js';

interface Props {
    id: string;
}

const toArray = (value: string | string[]): string[] =>
    Array.isArray(value) ? value : [value];

/**
 * Boot the Outbrain pixel.
 *
 * When `obApi` already exists the marketer id is appended to the existing one
 * instead of replacing it, which is how Outbrain supports several advertisers
 * sharing a page. `obApi.marketerId` doubles as the registration record, so no
 * separate guard is needed.
 */
export const loadOutbrainTag = (marketerId: string): void => {
    if (!isConfiguredPixelId(marketerId)) return;

    const w = getAdvertisingWindow();
    const existing = w.obApi;

    if (existing) {
        const marketerIds = toArray(existing.marketerId);
        if (!marketerIds.includes(marketerId)) {
            existing.marketerId = [...marketerIds, marketerId];
        }
        return;
    }

    function obApi(...args: unknown[]): void {
        if (obApi.dispatch) {
            obApi.dispatch(...args);
        } else {
            obApi.queue.push(args);
        }
    }

    obApi.dispatch = undefined as ObApiStub['dispatch'];
    obApi.queue = [] as unknown[][];
    obApi.version = '1.1';
    obApi.loaded = true;
    obApi.marketerId = marketerId as string | string[];

    w.obApi = obApi;
    injectHeadScript(OUTBRAIN_SRC);
};

export const OutbrainTrackerHead: React.FC<Props> = ({ id }) => {
    // Injected after hydration (see `injectHeadScript`).
    useEffect(() => {
        loadOutbrainTag(id);
    }, [id]);

    return null;
};
